// Built-in signals provider — a read-only view over the curated base set
// (exampleTriggers.js). Signals ship NATIVELY now: the dashboard no longer
// authors or persists them, so there is no localStorage layer and no version
// drift between a tenant's saved copy and the shipped set. Every signal is a
// built-in "default"; the API below is read-only. Entries carry identity only -
// a catalog detection has no client-side source (`steps` is empty); its
// definition is fetched from the backend when viewed (useSignalSource.js).
//
//   { id, command, title, description?, family, renderSource, steps[] }
//
// Kept as a thin module (rather than importing EXAMPLE_TRIGGERS at each call
// site) so the rest of the signals stack — the console, the per-signal lens,
// the sidebar dim, the trace-log overlay — keeps its listTriggers /
// subscribeTriggers shape unchanged. subscribeTriggers never fires (the set is
// static), but is retained so callers don't have to special-case it.

import { CATALOG_SIGNALS } from "./exampleTriggers.js";

export function sanitizeCommand(raw) {
    if (!raw) return "";
    return String(raw).trim().replace(/^\//, "")
        .toLowerCase().replace(/[^a-z0-9-_]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 64);
}

// The shipped signal set, normalized + sorted by command. Built once: the set
// is static, so the array and its entries keep a stable identity across reads —
// consumers can memo on it and their subscribe-on-mount snapshots never go
// stale.
const BUILTINS = CATALOG_SIGNALS
    .map((t) => ({
        id: t.id,
        command: sanitizeCommand(t.command),
        title: (t.title || "").trim() || sanitizeCommand(t.command),
        description: (t.description || "").trim() || undefined,
        renderSource: t.renderSource || null,
        // `family` mirrors the tag namespace the signal emits ("risk" | "pii" |
        // "audit"); the signals dim tiers `audit` below alerts in the console.
        family: t.family || "risk",
        steps: Array.isArray(t.steps) ? t.steps : [],
    }))
    .sort((a, b) => a.command.localeCompare(b.command));

const DEFAULT_COMMANDS = new Set(BUILTINS.map((t) => t.command));

// Every shipped signal is a built-in default (read-only). Kept so the UI can
// still badge "built-in default" and any defensive read-only guards hold.
export function isDefaultTrigger(command) {
    return DEFAULT_COMMANDS.has(sanitizeCommand(command));
}

// `tenantId` is accepted (and ignored) so call sites that passed it for the old
// tenant-scoped localStorage store keep working unchanged.
export function listTriggers(_tenantId) {
    return BUILTINS;
}

export function findTriggerByCommand(_tenantId, command) {
    const target = sanitizeCommand(command);
    if (!target) return null;
    return BUILTINS.find((t) => t.command === target) || null;
}

// The set is static, so this never notifies; retained so consumers keep their
// subscribe-on-mount shape without branching on whether signals can change.
export function subscribeTriggers() {
    return () => {};
}
